import numeralLib from 'numeral';
import { get, intersection } from 'lodash';
import moment, { LocaleSpecification } from 'moment';
import momentTimezone from 'moment-timezone';
import { IncomeDate } from 'services/dateFormatter';
import { NUMBERS_WITHOUT_ABBREVIATION_RANGE, CURRENCY_SIGN_POSITION } from 'appConstants';
import i18n from 'i18n';
import { localeMap } from 'utils/localeMap';
import { formatString } from '../utils';
import {
  SupportedCurrency,
  LocalizationConfigFromResponse,
  CurrentCurrencies,
  AvailableDataProviders,
  FormatNumberWithCurrencyInterface,
  LocalizationConfig,
  AdditionalSetting,
  DateTimeFormatFields,
  Delimiters
} from './types';
import { LocaleName } from './localeName';
import { getCookieByName } from '../cookies';

const removeZerosRegex = /\.00/g;

/* istanbul ignore next: this code for numeral js configuration */
const ordinals: { [key: string]: any } = {
  en: (n: number) => {
    const b = n % 10;
    return ~~((n % 100) / 10) === 1 ? 'th' : b === 1 ? 'st' : b === 2 ? 'nd' : b === 3 ? 'rd' : 'th';
  },
  fr: (n: number) => (n === 1 ? 'er' : 'e'),
  cs: () => '.',
  sk: () => '.',
  de: () => '.',
  ro: () => '.'
};

export const dataProviders = {
  transactions: 'transactions',
  mastercard: 'mastercard'
};

export const defaultLocale = 'en-US';

const defaultLocalizationConfig: LocalizationConfig = {
  'en-US': {
    currencyFormatter: '0,0.00',
    currencyFormatterLargeNumbers: '(0.00a)',
    stringWithCurrencyFormat: CURRENCY_SIGN_POSITION.before,
    delimiters: { thousands: ',', decimal: '.' },
    dateFormats: {
      hours: 'hh A',
      hoursAndMinutes: 'hh:mm A',
      dateMonthYear: 'MMMM DD, YYYY',
      monthDateYearBySlash: 'MM/DD/YYYY',
      monthDateYearByDash: 'YYYY-MM-DD',
      monthDateYearByDashShort: 'MM-DD-YY',
      monthShortYear: 'MMM YYYY',
      dateMonthShort: 'MMM DD',
      monthShort: 'MMM',
      weekDayShortMonthShortDateYear: 'ddd, MMM DD, YYYY',
      dayShort: 'ddd',
      dateMonthShortYear: 'MMM DD, YYYY',
      dateMonthShortYearHoursAndMinutesTimezone: 'MMM DD, YYYY, hh:mm A z',
      dateMonthShortYearHoursAndMinutes: 'MMM DD, YYYY, hh:mm A',
      monthShortYearShortSeparated: 'MMM ‘YY'
    }
  }
};

const defaultUSDCurrency = {
  currencyCode: 'USD',
  currencySymbol: '$',
  name: 'currencyNameUSD',
  description: '',
  image:
    // eslint-disable-next-line max-len
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -90 640 640"><g transform="matrix(1.25,0,0,-1.25,0,462.225)"><g transform="scale(0.1,0.1)"><path style="fill: #e9e6e5" d="m 18.1133,432.949 c 3.6953,-14.359 8.3984,-28.297 13.0937,-42.097 12.6563,-36.832 28.0157,-72.403 47.3594,-105.68 0.1484,-0.281 0.1484,-0.723 0.3008,-0.723 l 0.0664,0 C 177.492,115.059 358.828,0 568.891,0 L 4551.11,0 c 210.06,0 391.4,115.059 489.96,284.449 l 0.06,0 c 0.15,0 0.15,0.442 0.29,0.582 19.2,33.141 34.56,68.699 47.22,105.399 4.83,14.082 9.52,28.16 13.22,42.66 3.42,12.949 5.98,26.019 8.39,39.109 5.56,30.672 9.56,61.731 9.75,94.313 l 0,2562.378 c 0,33.14 -3.98,65.28 -9.81,96.71 -2.28,13.09 -4.98,26.17 -8.25,38.97 -3.84,14.65 -8.54,28.87 -13.37,42.95 -12.66,36.69 -27.88,72.11 -47.22,105.25 0,0.14 -0.14,0.43 -0.28,0.57 l 0.01,0 c -98.56,169.38 -279.89,284.44 -489.95,284.44 l -1991.13,0 0,-1991.11 -2560,0 L 0,568.891 C 0,535.762 4.01563,503.77 9.70313,472.352 12.1211,459.121 14.8398,445.891 18.1133,432.949" /><path style="fill: #c82239" d="m 5041.08,3413.34 c -98.56,169.38 -279.89,284.44 -489.95,284.44 l -1991.13,0 0,-284.44 2481.08,0" /><path style="fill: #c82239" d="m 0,568.891 5120,0 0,284.449 -5120,0 0,-284.449" /><path style="fill: #c82239" d="m 2560,1706.67 2560,0 0,284.438 -2560,0 0,-284.438 z" /><path style="fill: #c82239" d="m 2560,2275.56 2560,0 0,284.441 -2560,0 0,-284.441 z" /><path style="fill: #c82239" d="m 0,1137.78 5120,0 0,284.438 -5120,0 0,-284.438 z" /><path style="fill: #c82239" d="m 4551.11,0 c 210.06,0 391.4,115.059 489.96,284.449 l -4962.1364,0 C 177.492,115.059 358.828,0 568.891,0 L 4551.11,0" /><path style="fill: #c82239" d="m 2560,2844.45 2560,0 0,284.441 -2560,0 0,-284.441 z" /><path style="fill: #e9e6e5" d="m 0,3128.89 c 0,33.14 4.26563,65 9.67188,96.57 C 3.98047,3194.03 0,3162.03 0,3128.89" /><path style="fill: #e9e6e5" d="m 78.8906,3413.34 c -0.1406,-0.29 -0.2851,-0.43 -0.2851,-0.71 0.1445,0.28 0.1445,0.42 0.2851,0.71" /><path style="fill: #e9e6e5" d="m 18.1641,3264.8 c 3.5547,14.5 8.5312,28.3 13.082,42.24 -4.6914,-13.94 -9.3867,-27.88 -13.082,-42.24" /><path style="fill: #3f3972" d="m 2560,3697.78 -1991.109,0 C 254.719,3697.78 0,3443.06 0,3128.89 l 0,-1422.22 2560,0 0,1991.11" /><path style="fill: #ffffff" d="m 284.645,3310.18 87.894,-63.86 -33.566,-103.26 87.754,63.86 87.89,-63.86 -33.707,103.26 88.035,63.86 -108.656,0 -33.562,103.25 -33.422,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 569.09,3025.73 87.894,-63.86 -33.707,-103.25 87.895,63.86 87.891,-63.86 -33.708,103.25 88.036,63.86 -108.657,0 -33.562,103.25 -33.426,-103.25 -108.656,0" /><path style="fill: #ffffff" d="m 1137.98,3025.73 87.89,-63.86 -33.7,-103.25 87.89,63.86 87.89,-63.86 -33.71,103.25 88.04,63.86 -108.66,0 -33.56,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 1706.87,3025.73 87.89,-63.86 -33.71,-103.25 87.9,63.86 87.89,-63.86 -33.71,103.25 88.04,63.86 -108.66,0 -33.56,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 569.09,2456.81 87.894,-63.86 -33.707,-103.25 87.895,63.85 87.891,-63.85 -33.708,103.25 88.036,63.86 -108.657,0 -33.562,103.25 -33.426,-103.25 -108.656,0" /><path style="fill: #ffffff" d="m 1137.98,2456.81 87.89,-63.86 -33.7,-103.25 87.89,63.85 87.89,-63.85 -33.71,103.25 88.04,63.86 -108.66,0 -33.56,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 1706.87,2456.81 87.89,-63.86 -33.71,-103.25 87.9,63.85 87.89,-63.85 -33.71,103.25 88.04,63.86 -108.66,0 -33.56,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 853.535,3310.18 87.891,-63.86 -33.703,-103.26 87.89,63.86 87.897,-63.86 -33.71,103.26 88.04,63.86 -108.66,0 -33.567,103.25 -33.422,-103.25 -108.656,0" /><path style="fill: #ffffff" d="m 1422.42,3310.18 87.9,-63.86 -33.71,-103.26 87.89,63.86 87.9,-63.86 -33.71,103.26 88.04,63.86 -108.66,0 -33.57,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 1991.31,3310.18 87.9,-63.86 -33.71,-103.26 87.89,63.86 87.9,-63.86 -33.71,103.26 88.03,63.86 -108.65,0 -33.57,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 284.645,2741.25 87.894,-63.86 -33.566,-103.25 87.754,63.86 87.89,-63.86 -33.707,103.25 88.035,63.86 -108.656,0 -33.562,103.25 -33.422,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 907.777,2574.17 87.895,63.85 87.748,-63.85 -33.56,103.25 87.89,63.86 -108.51,0 -33.568,103.11 -33.563,-103.11 -108.515,0 87.75,-63.86 -33.567,-103.25" /><path style="fill: #ffffff" d="m 1422.42,2741.25 87.9,-63.86 -33.71,-103.25 87.89,63.86 87.9,-63.86 -33.71,103.25 88.04,63.86 -108.66,0 -33.57,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 1991.31,2741.25 87.9,-63.86 -33.71,-103.25 87.89,63.86 87.9,-63.86 -33.71,103.25 88.03,63.86 -108.65,0 -33.57,103.25 -33.42,-103.25 -108.66,0" /><path style="fill: #ffffff" d="m 284.645,2172.36 87.894,-63.85 -33.566,-103.26 87.754,63.86 87.89,-63.86 -33.707,103.25 88.035,63.86 -108.656,0 -33.562,103.26 -33.422,-103.26 -108.66,0 0,0" /><path style="fill: #ffffff" d="m 907.777,2005.28 87.895,63.85 87.748,-63.85 -33.56,103.25 87.89,63.86 -108.51,0 -33.568,103.11 -33.563,-103.11 -108.515,0 87.75,-63.86 -33.567,-103.25" /><path style="fill: #ffffff" d="m 1422.42,2172.36 87.9,-63.85 -33.71,-103.26 87.89,63.86 87.9,-63.86 -33.71,103.25 88.04,63.86 -108.66,0 -33.57,103.26 -33.42,-103.26 -108.66,0 0,0" /><path style="fill: #ffffff" d="m 1991.31,2172.36 87.9,-63.85 -33.71,-103.26 87.89,63.86 87.9,-63.86 -33.71,103.25 88.03,63.86 -108.65,0 -33.57,103.26 -33.42,-103.26 -108.66,0 0,0" /></g></g></svg>',
  isDefault: true
};

const danubeLocales = ['de-AT', 'cs-CZ', 'ro-RO', 'sk-SK', 'de-DE'];
const defaultLanguage = 'en-US';
const sourceLocales = ['en-US', 'en_US', 'en-GB', 'en_GB'];

class Localization {
  private locale: string = defaultLocale;

  private numeralLib = numeralLib;

  private supportedCurrencies: SupportedCurrency[] = [defaultUSDCurrency];

  private localizationConfig: LocalizationConfig = defaultLocalizationConfig as LocalizationConfig;

  private currentCurrencies: CurrentCurrencies = {
    transactions: [],
    mastercard: []
  };

  public init(supportedCurrencies: SupportedCurrency[], localizationConfig: LocalizationConfigFromResponse): void {
    try {
      const selectedLocale = this.getLocale();
      const receivedLocales = Object.keys(localizationConfig);
      if (!receivedLocales.includes(selectedLocale)) {
        const receivedDefaultLocale = Object.entries(localizationConfig).reduce(
          (currentLocale, [locale, data]) => (data && data.isDefault ? locale : currentLocale),
          receivedLocales[0] || defaultLocale
        );
        this.setLocale(receivedDefaultLocale);
        // eslint-disable-next-line no-console
        console.warn(
          `Locale ${selectedLocale} not received in localizationConfig. Selected locale ${receivedDefaultLocale}`
        );
      }

      Object.entries(localizationConfig).forEach(([locale, { numbersFormatting, dateFormats, additionalSettings }]) => {
        const language = this.getLanguage(locale);
        const config = {
          delimiters: numbersFormatting.delimiters,
          abbreviations: numbersFormatting.abbreviations,
          ordinal: ordinals[language],
          currency: { symbol: '' }
        };
        this.numeralLib.register('locale', locale, config);
        this.localizationConfig = {
          ...this.localizationConfig,
          [locale]: {
            currencyFormatter: numbersFormatting.currencyFormatter,
            delimiters: numbersFormatting.delimiters,
            currencyFormatterLargeNumbers: numbersFormatting.currencyFormatterLargeNumbers,
            stringWithCurrencyFormat: numbersFormatting.stringWithCurrencyFormat,
            dateFormats,
            additionalSettings
          }
        };
      });
      this.supportedCurrencies = supportedCurrencies;

      const locale = this.getLocale();
      const language = this.getLanguage();
      const { momentSettings } = localizationConfig[locale] || { momentSettings: false };

      if (momentSettings) {
        moment.updateLocale(language, this.extendMomentSettings(momentSettings));
      } else {
        // eslint-disable-next-line max-len,no-console
        console.warn(
          `Moment initialised with default configuration for "${language}" language, due to empty momentSettings in the "${locale}" locale.`
        );
      }
    } catch (e) {
      console.error(`Localization service initialization error:\n ${e}`);
    }
  }

  private extendMomentSettings(momentSettings: LocaleSpecification): LocaleSpecification {
    if (momentSettings.months) {
      const format = get(momentSettings, 'months.format');
      const standalone = get(momentSettings, 'months.standalone');
      if (format && standalone) {
        const months = {
          format,
          standalone,
          // eslint-disable-next-line no-useless-escape
          isFormat: /D[oD]?(\[[^\[\]]*\]|\s+)+MMMM?|MMMM?(\[[^\[\]]*\]|\s+)+D[oD]?/
        };
        return { ...momentSettings, months };
      }
    }
    return momentSettings;
  }

  public getDateTimeFormat(name: string): string {
    return get(
      this.localizationConfig,
      [this.getLocale(), 'dateFormats', name],
      get(defaultLocalizationConfig, [defaultLocale, 'dateFormats', name], '')
    );
  }

  public customFormatDate(date: IncomeDate, formatKeys: DateTimeFormatFields[], delimiter: string = ' '): string {
    const format = formatKeys.map(item => this.getDateTimeFormat(item)).join(delimiter);
    return moment.isMoment(date) ? date.format(format) : moment(date).format(format);
  }

  public formatDate(date: IncomeDate, formatName: string): string {
    const format = this.getDateTimeFormat(formatName);

    return moment.isMoment(date) ? date.format(format) : moment(date).format(format);
  }

  public setCurrentCurrencies(type: string, currencyCodes: string[]): void {
    this.currentCurrencies[type] = currencyCodes;
  }

  public getLocale(): string {
    return this.locale;
  }

  public getLanguage(locale?: string): string {
    if (locale === defaultLocale || this.locale === defaultLocale) {
      return 'en';
    }

    return locale ? localeMap[locale].toLowerCase() : localeMap[this.locale].toLowerCase();
  }

  public setLocale(locale: string): void {
    this.locale = locale;
    this.numeralLib.locale(this.locale);
    moment.locale(this.locale);
    momentTimezone.locale(this.locale);
  }

  private getDefaultCurrency(): SupportedCurrency {
    return this.supportedCurrencies.find(currency => currency.isDefault) || defaultUSDCurrency;
  }

  public getCurrencySettings(dataProvider?: AvailableDataProviders): SupportedCurrency {
    const defaultCurrency = this.getDefaultCurrency();
    if (!dataProvider) return defaultCurrency;

    const currencies = this.currentCurrencies[dataProvider];
    const supportedCurrenciesMap = new Map();
    this.supportedCurrencies.forEach(currency => supportedCurrenciesMap.set(currency.currencyCode, currency));

    if (currencies && currencies.every(currency => supportedCurrenciesMap.has(currency))) {
      if (currencies.length === 1) return supportedCurrenciesMap.get(currencies[0]);

      if (currencies.length > 1) return supportedCurrenciesMap.get('AbsoluteValue') || defaultCurrency;
    }

    return defaultCurrency;
  }

  public getCurrencySymbol(dataProvider?: AvailableDataProviders): string {
    return this.getCurrencySettings(dataProvider).currencySymbol;
  }

  public getCurrencySignPosition(): CURRENCY_SIGN_POSITION {
    return this.localizationConfig[this.getLocale()].stringWithCurrencyFormat;
  }

  public formatStringWithCurrency(value: string, dataProvider?: AvailableDataProviders) {
    const currencySignPosition = this.getCurrencySignPosition();
    const str = currencySignPosition === CURRENCY_SIGN_POSITION.before ? `{0}${value}` : `${value} {0}`;

    return formatString(str, this.getCurrencySymbol(dataProvider));
  }

  public formatNumberWithCurrency({
    value,
    dataProvider,
    isFixed,
    removeDecimalForIntegers
  }: FormatNumberWithCurrencyInterface): string {
    const formattedNumber = this.formatNumber(value, isFixed, { removeDecimalForIntegers });
    return this.formatStringWithCurrency(formattedNumber, dataProvider).join('');
  }

  public getAdditionalSettingsValue<K extends keyof AdditionalSetting>(param: string): AdditionalSetting[K] {
    const localeSettings = this.localizationConfig[this.getLocale()] || this.localizationConfig[defaultLocale];
    const { additionalSettings } = localeSettings;

    return additionalSettings && additionalSettings[param];
  }

  public formatPercent = (value: string): string => `${value}`.replace(/^(-?)0+/, '$1');

  public formatNumber(
    value: number,
    isFixed?: boolean,
    options?: {
      removeDecimalForIntegers?: boolean;
    }
  ): string {
    const localeSettings = this.localizationConfig[this.getLocale()] || this.localizationConfig[defaultLocale];
    const formatter =
      value <= -NUMBERS_WITHOUT_ABBREVIATION_RANGE.max || value >= NUMBERS_WITHOUT_ABBREVIATION_RANGE.max
        ? localeSettings.currencyFormatterLargeNumbers
        : localeSettings.currencyFormatter;

    const shouldRemoveDecimalForInteger = !!options && options?.removeDecimalForIntegers && Number.isInteger(value);
    const shouldShowDecimals = shouldRemoveDecimalForInteger ? false : isFixed;

    const result = shouldShowDecimals
      ? this.numeralLib(value).format(formatter)
      : this.numeralLib(value).format(formatter.replace(removeZerosRegex, ''));

    return result;
  }

  public formatCounts(value: number): string {
    return this.numeralLib(value).format('0,0');
  }

  public async checkLocaleIsValid() {
    const localeInStore = new LocaleName(this.getLocale()).ISOName;
    const localeInCookie = new LocaleName(getCookieByName('locale')).ISOName;

    if (localeInCookie && localeInCookie !== localeInStore) {
      await i18n.changeLanguage(this.getDetectedLanguage());
    }
  }

  public getDanubeRegionLocale(): string {
    const locale = this.getLocale();
    const locales = Object.keys(this.localizationConfig);
    const regionLocale = locale === 'en-GB' ? intersection(locales, danubeLocales).shift() : locale;
    return regionLocale || locale;
  }

  private getLocaleFromCookies(): string {
    const locale =
      document.cookie
        .split('; ')
        .find(row => row.startsWith('locale='))
        ?.split('=')[1] || this.getLocale();
    return locale;
  }

  public getDetectedLanguage(): string {
    const locale = this.getLocaleFromCookies();
    const sourceLocale = getCookieByName('sourceLocale');

    this.setLocale(locale);
    if (locale && sourceLocales.includes(locale) && sourceLocale) {
      return sourceLocale;
    }
    if (locale && localeMap[locale]) {
      return localeMap[locale];
    }
    return defaultLanguage;
  }

  public getLanguageFromLocale(): string {
    return this.locale.substr(0, 2).toLowerCase();
  }

  public getConfigNumberDelimiters(): Delimiters {
    const localeSettings = this.localizationConfig[this.getLocale()] || this.localizationConfig[defaultLocale];
    return localeSettings.delimiters;
  }
}

export const localizationService = new Localization();
